import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Loop, Info } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: "#E0F7FA",
    borderBottom: `2px solid #00acc1`,
  },
  icon: {
    color: "#00acc1",
    width: 28,
    height: 28,
  },
  content: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  previewBox: {
    backgroundColor: "#F5F5F5",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: "#E3F2FD",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid #90CAF9`,
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
  },
  variableChip: {
    backgroundColor: "rgba(0, 172, 193, 0.15)",
    color: "#00acc1",
    padding: "2px 8px",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: "12px",
  },
}));

const FlowBuilderAddForModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const [arrayVariable, setArrayVariable] = useState("");
  const [itemVariable, setItemVariable] = useState("item");
  const [indexVariable, setIndexVariable] = useState("index");

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setArrayVariable(data.data.arrayVariable || "");
      setItemVariable(data.data.itemVariable || "item");
      setIndexVariable(data.data.indexVariable || "index");
    } else if (open === "create") {
      setArrayVariable("");
      setItemVariable("item");
      setIndexVariable("index");
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setArrayVariable("");
    setItemVariable("item");
    setIndexVariable("index");
  };

  const handleSave = () => {
    if (!arrayVariable.trim()) {
      alert("Por favor, defina a variável do array");
      return;
    }

    if (!itemVariable.trim()) {
      alert("Por favor, defina o nome da variável do item");
      return;
    }

    const formData = {
      arrayVariable: arrayVariable.trim(),
      itemVariable: itemVariable.trim(),
      indexVariable: indexVariable.trim() || "index",
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: formData,
      });
    } else {
      onSave(formData);
    }
    handleClose();
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Loop className={classes.icon} />
          <Typography variant="h6">
            {open === "edit" ? "Editar For (Loop)" : "Novo For (Loop)"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          O For itera sobre um array/lista, executando o fluxo conectado à saída
          "Loop" para cada item. Quando termina, segue pela saída "Fim".
        </Typography>

        {/* Array Variable */}
        <Box className={classes.section} mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Array a ser iterado *
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: ${produtos} ou ${contatos}"
            value={arrayVariable}
            onChange={(e) => setArrayVariable(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Typography className={classes.helperText}>
            Use {"${variavel}"} para referenciar uma variável do fluxo que contenha um array.
            Pode ser o resultado de uma consulta SQL, uma lista de contatos, etc.
          </Typography>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        {/* Item Variable */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" gutterBottom>
            Nome da variável do item *
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: item, produto, contato"
            value={itemVariable}
            onChange={(e) => setItemVariable(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Typography className={classes.helperText}>
            Nome da variável que armazenará o item atual da iteração.
            Acesse com <span className={classes.variableChip}>${"{" + (itemVariable || "item") + "}"}</span>
          </Typography>
        </Box>

        {/* Index Variable */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" gutterBottom>
            Nome da variável do índice
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: index, i, contador"
            value={indexVariable}
            onChange={(e) => setIndexVariable(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Typography className={classes.helperText}>
            Nome da variável que armazenará o índice atual (0, 1, 2...).
            Acesse com <span className={classes.variableChip}>${"{" + (indexVariable || "index") + "}"}</span>
          </Typography>
        </Box>

        {/* Info Box */}
        <Paper className={classes.infoBox} elevation={0}>
          <Info style={{ color: "#1976d2", width: 20, height: 20, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 4 }}>
              Variáveis disponíveis no loop:
            </Typography>
            <Typography variant="caption" component="div" style={{ marginBottom: 2 }}>
              • <span className={classes.variableChip}>${"{" + (itemVariable || "item") + "}"}</span> - Item atual da iteração
            </Typography>
            <Typography variant="caption" component="div" style={{ marginBottom: 2 }}>
              • <span className={classes.variableChip}>${"{" + (indexVariable || "index") + "}"}</span> - Índice atual (começa em 0)
            </Typography>
            <Typography variant="caption" component="div">
              • <span className={classes.variableChip}>{"${total}"}</span> - Total de itens no array
            </Typography>
          </Box>
        </Paper>

        {/* Preview */}
        <Paper className={classes.previewBox} elevation={0}>
          <Typography variant="subtitle2" gutterBottom>
            Prévia:
          </Typography>
          <Box style={{ fontFamily: "monospace", fontSize: "12px" }}>
            <div style={{ color: "#00acc1", fontWeight: "bold" }}>
              for ({itemVariable || "item"}, {indexVariable || "index"}) in {arrayVariable || "array"} {"{"}
            </div>
            <div style={{ paddingLeft: 16, color: "#666" }}>
              // Saída "Loop" - executa para cada item
            </div>
            <div style={{ color: "#00acc1", fontWeight: "bold" }}>{"}"}</div>
            <div style={{ color: "#4caf50", marginTop: 4 }}>
              // Saída "Fim" - quando termina o loop
            </div>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          style={{ backgroundColor: "#00acc1", color: "#fff" }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddForModal;
